import { useDroppable, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { AlertCircle, GripVertical, Trash2 } from 'lucide-react'
import type { FormField, FormFieldType } from '@kfolks/types'
import { useFormBuilderState } from './FormBuilderState'

/**
 * Form canvas — the central authoring surface for the visual form
 * builder. Renders the in-progress `formSchema.fields[]` as a vertical
 * stack of sortable cards. Accepts two distinct drag flows:
 *
 *   1. **Palette drop** — a `useDraggable` item from `FieldPalette`
 *      (data: `{ source: 'palette', fieldType }`) lands on the canvas.
 *      The canvas appends a new field of that type to the in-progress
 *      schema, generates a unique id, and selects the new field so the
 *      properties panel (task 7.4) opens on it.
 *
 *   2. **Internal reorder** — a `useSortable` card lands on another
 *      card's slot. The canvas calls `reorderField(fromId, toId)` from
 *      the shared `FormBuilderState` context.
 *
 * Both flows funnel through `handleDragEnd` (mounted on the parent
 * `DndContext` in `FormBuilderPage`); see `dispatchDragEnd` below for
 * the dispatch logic.
 *
 * Empty-state: when the schema has no fields, the canvas renders an
 * explicit drop-target placeholder with a hint to drag a palette item.
 *
 * Selected card highlight: clicking a card sets it as the selected
 * field; the properties panel (forthcoming) will read this from the
 * shared state.
 *
 * Per design NFR5-001 + NFR5-003: drag-feedback latency is provided by
 * `@dnd-kit/core` (well under 100ms on modern laptops); keyboard sensors
 * are enabled at the `DndContext` level for non-mouse reordering.
 */
export function FormCanvas() {
	const { formSchema, selectedFieldId, removeField, selectField, validation } = useFormBuilderState()
	const { setNodeRef: setDropRef, isOver } = useDroppable({
		id: 'form-canvas-drop-zone',
		data: { kind: 'canvas' },
	})

	const isEmpty = formSchema.fields.length === 0

	return (
		<div className="space-y-2">
			{validation.topLevelErrors.length > 0 ? (
				<div
					role="alert"
					data-testid="canvas-top-level-errors"
					className="space-y-1 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
				>
					{validation.topLevelErrors.map((msg, i) => (
						<div key={i} className="flex items-start gap-2">
							<AlertCircle className="mt-0.5 size-4 shrink-0" />
							<p className="text-xs">{msg}</p>
						</div>
					))}
				</div>
			) : null}

			<div
				ref={setDropRef}
				role="region"
				aria-label="Form canvas"
				data-canvas-drop-target="true"
				className={
					'rounded-md border bg-card p-4 transition-colors ' +
					(isOver ? 'border-primary bg-primary/5' : 'border-border')
				}
			>
				{isEmpty ? (
					<EmptyCanvasState />
				) : (
					<SortableContext items={formSchema.fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
						<ul className="space-y-2" role="list">
							{formSchema.fields.map((field) => (
								<FieldCard
									key={field.id}
									field={field}
									selected={field.id === selectedFieldId}
									errors={validation.errorsByFieldId[field.id] ?? []}
									onSelect={() => selectField(field.id)}
									onRemove={() => removeField(field.id)}
								/>
							))}
						</ul>
					</SortableContext>
				)}
			</div>
		</div>
	)
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyCanvasState() {
	return (
		<div className="flex min-h-40 flex-col items-center justify-center rounded-md border-2 border-dashed border-muted p-8 text-center text-sm text-muted-foreground">
			<p className="font-medium">Drop a field from the palette to begin</p>
			<p className="mt-1">Drag any field type from the left panel onto this area.</p>
		</div>
	)
}

// ─── Single sortable field card ──────────────────────────────────────────────

function FieldCard({
	field,
	selected,
	errors,
	onSelect,
	onRemove,
}: {
	field: FormField
	selected: boolean
	errors: ReadonlyArray<string>
	onSelect: () => void
	onRemove: () => void
}) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: field.id,
		data: { source: 'canvas', fieldId: field.id },
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	}

	const hasErrors = errors.length > 0
	return (
		<li ref={setNodeRef} style={style}>
			<div
				role="button"
				tabIndex={0}
				onClick={onSelect}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						onSelect()
					}
				}}
				aria-pressed={selected}
				aria-label={`Field ${field.label || field.id}`}
				aria-invalid={hasErrors || undefined}
				data-field-id={field.id}
				data-field-type={field.type}
				data-has-errors={hasErrors || undefined}
				className={
					'flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm transition-colors hover:bg-accent ' +
					(selected ? 'border-primary ring-1 ring-primary ' : 'border-border ') +
					(hasErrors ? 'border-destructive ring-1 ring-destructive/40' : '')
				}
			>
				<button
					type="button"
					className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-accent active:cursor-grabbing"
					aria-label={`Drag to reorder ${field.label || field.id}`}
					{...listeners}
					{...attributes}
				>
					<GripVertical className="size-4" />
				</button>

				<div className="flex flex-1 flex-col">
					<span className="font-medium">{field.label || field.id}</span>
					<span className="font-mono text-xs text-muted-foreground">
						{field.id} · {field.type}
					</span>
					{hasErrors ? (
						<ul
							role="alert"
							data-testid={`field-errors-${field.id}`}
							className="mt-1 space-y-0.5 text-xs text-destructive"
						>
							{errors.map((msg, i) => (
								<li key={i} className="flex items-start gap-1">
									<AlertCircle className="mt-0.5 size-3 shrink-0" />
									<span>{msg}</span>
								</li>
							))}
						</ul>
					) : null}
				</div>

				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation()
						onRemove()
					}}
					aria-label={`Remove ${field.label || field.id}`}
					className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
				>
					<Trash2 className="size-4" />
				</button>
			</div>
		</li>
	)
}

// ─── Drag-end dispatcher (mounted by FormBuilderPage on its DndContext) ──────

/**
 * Build the `onDragEnd` handler for the parent `DndContext`. Lives here
 * (next to the canvas) so the dispatch logic stays close to the consumer.
 *
 * Two paths:
 *   - palette → canvas: insert the dragged `fieldType` (defaults to end
 *     of list when dropped on the empty canvas).
 *   - canvas → canvas: reorder the dragged field over the target field.
 *
 * Returns the new field's id when a palette drop occurred (so the caller
 * can immediately select it and open the properties panel on it).
 */
export interface DragEndDispatch {
	insertField: (fieldType: FormFieldType, atIndex?: number) => string
	reorderField: (fromId: string, toId: string) => void
	selectField: (id: string | null) => void
}

export function dispatchDragEnd(event: DragEndEvent, deps: DragEndDispatch): void {
	const { active, over } = event
	if (!over) return

	const activeData = active.data.current as { source?: string; fieldType?: FormFieldType; fieldId?: string } | undefined
	const overData = over.data.current as { kind?: string; source?: string; fieldId?: string } | undefined

	// Palette → canvas (drop target either the canvas itself or a card on it).
	if (activeData?.source === 'palette' && activeData.fieldType) {
		const newId = deps.insertField(activeData.fieldType)
		deps.selectField(newId)
		return
	}

	// Canvas → canvas reorder.
	if (activeData?.source === 'canvas' && activeData.fieldId && overData?.fieldId) {
		if (activeData.fieldId !== overData.fieldId) {
			deps.reorderField(activeData.fieldId, overData.fieldId)
		}
	}
}
